import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
  type Placement,
  type ReferenceType,
} from '@floating-ui/react';
import {
  cloneElement,
  isValidElement,
  useEffect,
  type CSSProperties,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { cx, useControllableState } from '../utils.ts';
import './overlay.css';

export interface PopoverApi {
  close(): void;
}

export interface PopoverProps {
  /** Element that toggles the popover on click. Omit when using `anchor`. */
  trigger?: ReactElement;
  /** Position against an arbitrary element (or virtual element) instead of a trigger. */
  anchor?: ReferenceType | null;
  children: ReactNode | ((api: PopoverApi) => ReactNode);
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: Placement;
  offset?: number;
  /** Make the panel at least as wide as the anchor. */
  matchWidth?: boolean;
  width?: number | string;
  maxHeight?: number;
  padding?: 'none' | 'sm' | 'md';
  className?: string;
  style?: CSSProperties;
  /** Tabbable index to focus when opening, `-1` to focus the panel itself, or a ref. */
  initialFocus?: number | MutableRefObject<HTMLElement | null>;
  returnFocus?: boolean;
  /** Trap focus inside (use for forms). */
  modal?: boolean;
  /** Close when clicking outside (default true). Return false from a function to keep open. */
  closeOnOutsidePress?: boolean | ((e: MouseEvent) => boolean);
  role?: 'dialog' | 'listbox' | 'menu';
}

export function Popover({
  trigger,
  anchor,
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom-start',
  offset: gap = 6,
  matchWidth,
  width,
  maxHeight,
  padding = 'sm',
  className,
  style,
  initialFocus = 0,
  returnFocus = true,
  modal = false,
  closeOnOutsidePress = true,
  role = 'dialog',
}: PopoverProps) {
  const [open, setOpen] = useControllableState(openProp, defaultOpen, onOpenChange);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(gap),
      flip({ padding: 10, fallbackAxisSideDirection: 'end' }),
      shift({ padding: 10 }),
      sizeMiddleware({
        padding: 10,
        apply({ availableHeight, rects, elements }) {
          elements.floating.style.setProperty('--popover-available-height', `${Math.max(120, availableHeight)}px`);
          if (matchWidth) elements.floating.style.minWidth = `${rects.reference.width}px`;
        },
      }),
    ],
  });

  useEffect(() => {
    if (anchor !== undefined) refs.setPositionReference(anchor);
  }, [anchor, refs]);

  const click = useClick(context, { enabled: !!trigger });
  const dismiss = useDismiss(context, { outsidePress: closeOnOutsidePress });
  const roleProps = useRole(context, { role });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, roleProps]);
  const { isMounted, status } = useTransitionStatus(context, { duration: { open: 160, close: 110 } });

  const triggerProps = (isValidElement(trigger) ? trigger.props : {}) as Record<string, unknown> & { ref?: Ref<Element> };
  const triggerRef = useMergeRefs([refs.setReference, triggerProps.ref]);

  const api: PopoverApi = { close: () => setOpen(false) };

  return (
    <>
      {trigger && cloneElement(trigger, getReferenceProps({ ...triggerProps, ref: triggerRef } as Record<string, unknown>))}
      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={modal}
            initialFocus={initialFocus === -1 ? 0 : initialFocus}
            order={initialFocus === -1 ? ['floating', 'content'] : ['content']}
            returnFocus={returnFocus}
          >
            <div
              ref={refs.setFloating}
              className={cx('g-popover', className)}
              data-status={status}
              data-padding={padding}
              style={{ ...floatingStyles, width, maxHeight: maxHeight ?? 'var(--popover-available-height)', ...style }}
              {...getFloatingProps()}
            >
              {typeof children === 'function' ? children(api) : children}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
